import { useCallback, useEffect, useMemo, useState } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { convertDistance, getDistance } from "geolib"

import { useConfigContext } from "@app/providers/config"
import { useCore } from "@app/hooks/useCore"
import { useFunctions } from "@app/hooks/useFunctions"
import { useImage } from "@app/hooks/useImage"
import { useRoutes } from "@app/hooks/useRoutes"
import { useStorage } from "@app/hooks/useStorage"

import type { ImageProps, LinkProps } from "@root/types/global"
import { GeocodeTypes } from "@app/api/geocode"

type LatLng = {
  lat: number
  lng: number
}

export type Store = GatsbyTypes.SanityStoreFragmentFragment & {
  key: string
  distance: number
  link: LinkProps
  imagePrimary?: ImageProps
  imageSecondary?: ImageProps
  stock: number
}

type ProductPayload = {
  stockWithBuffer: number
}

type StorePayload = {
  products: Array<ProductPayload>
  storeId: string
}

type StockPayload = {
  stores: Array<StorePayload> | null
}

type GeocodeResponse = {
  status: "success" | "error"
  body: GeocodeResult
}

type GeocodeResult = {
  address?: string
  bbox?: Array<number>
  center: Array<number>
  context: Array<{
    id: string
    mapbox_id: string
    text: string
    short_code?: string
    wikidata: string
  }>
  geometry: {
    coordinates: Array<number>
    type: string
  }
  id: string
  place_name: string
  place_type: Array<string>
  properties: Array<{
    accuracy?: number
    mapbox_id: string
  }>
  relevance: number
  text: string
  type: string
}

export type UseStores = {
  errors: Array<string>
  handleAll: () => void
  handleChange: (event: React.BaseSyntheticEvent) => void
  handleGeolocate: () => Promise<void>
  handleSubmit: (event: React.BaseSyntheticEvent) => void
  isLoading: boolean
  position: LatLng | null
  search: string
  searched: string
  storesWithStock: Array<Store>
  storesWithinSearch: Array<Omit<Store, "stock">>
}

export const useStores = (sku = ""): UseStores => {
  const { stores: allStores } = useStaticQuery<GatsbyTypes.StaticStoresQuery>(graphql`
    query StaticStores {
      stores: allSanityStore(filter: { available: { eq: true } }) {
        edges {
          node {
            ...SanityStoreFragment
          }
        }
      }
    }
    fragment SanityStoreFragment on SanityStore {
      key: _id
      title
      handle {
        current
      }
      storeId
      available
      url
      phone
      eventDescription: _rawEventDescription(resolveReferences: { maxDepth: 2 })
      eventLink: _rawEventLink(resolveReferences: { maxDepth: 2 })
      imagePrimary: _rawImagePrimary(resolveReferences: { maxDepth: 2 })
      imageSecondary: _rawImageSecondary(resolveReferences: { maxDepth: 2 })
      address {
        city
        street
      }
      hours {
        key: _key
        day
        open
        close
        storeClosed
      }
      location {
        lat
        lng
      }
      services
    }
  `)
  const {
    services: { location: configLocation },
    settings: { keys },
  } = useConfigContext()
  const {
    helpers: { edgeNormaliser },
  } = useCore()
  const { callFunction } = useFunctions()
  const { getGatsbyImage } = useImage()
  const { urlResolver } = useRoutes()
  const { storage } = useStorage()
  const [errors, setErrors] = useState<Array<string>>([])
  const [search, setSearch] = useState<string>(storage.get(keys.place) || "")
  const [searched, setSearched] = useState<string>(search)
  const [showAll, setShowAll] = useState<boolean>(!sku && !search ? true : false)
  const [isLoading, setLoading] = useState<boolean>(false)
  const [position, setPosition] = useState<LatLng | null>(null)
  const [storesWithStock, setStoresWithStock] = useState<Array<Store>>([])

  const distance = useMemo(() => (sku ? 100 : 25), [sku])

  const handleAll = useCallback(() => {
    setShowAll(true)
    setSearch("")
    setSearched("")
  }, [setSearch, setSearched, setShowAll])

  const handleDistance = useCallback(
    (position, location) =>
      location && position
        ? Math.round(
            (convertDistance(
              getDistance({ latitude: position.lat, longitude: position.lng }, { latitude: location.lat, longitude: location.lng }),
              `km`
            ) +
              Number.EPSILON) *
              100
          ) / 100
        : 0,
    []
  )
  const formatStoreTime = (input: string) => {
    const hours = input.substring(0, 2)
    const minutes = input.substring(2)
    return `${hours}:${minutes}`
  }

  const storesWithinSearch: Array<Omit<Store, "stock">> = useMemo(
    () =>
      (edgeNormaliser(allStores) as Array<GatsbyTypes.SanityStoreFragmentFragment>)
        ?.filter(({ location }: GatsbyTypes.SanityStoreFragmentFragment) => location && location?.lat !== null && location?.lng !== null)
        ?.filter(({ location }: GatsbyTypes.SanityStoreFragmentFragment) =>
          showAll ? true : position && handleDistance(position, location) && handleDistance(position, location) < distance
        )
        ?.map((store: GatsbyTypes.SanityStoreFragmentFragment) => {
          return {
            ...store,
            distance: handleDistance(position, store.location),
            imagePrimary: getGatsbyImage(store?.imagePrimary, { width: 500 }),
            imageSecondary: getGatsbyImage(store?.imageSecondary, { width: 500 }),
            link: urlResolver(store),
            hours: store.hours?.map(hour => ({
              close: formatStoreTime(hour?.close),
              open: formatStoreTime(hour?.open),
              day: hour?.day,
              key: hour?.key,
              storeClosed: hour?.storeClosed,
            })),
          }
        })
        ?.sort((a, b) => (showAll ? a.title - b.title : a.distance - b.distance)),
    [allStores, distance, edgeNormaliser, getGatsbyImage, handleDistance, position, showAll, urlResolver]
  )

  const handleChange = useCallback(({ target: { value } }) => setSearch(value), [setSearch])

  const handlePlace = useCallback(
    async (place: GeocodeResult) => {
      if (place?.geometry?.coordinates.length === 2) {
        setPosition(null)
        const position = {
          lng: place.geometry.coordinates[0],
          lat: place.geometry.coordinates[1],
        }

        if (place?.place_name) {
          storage.set(keys.place, place.place_name, 7)
          setSearched(place.place_name)
          setSearch(place.place_name)
        }
        setPosition(position)
        setLoading(false)
      }

      if (!sku) setLoading(false)
    },
    [keys, setLoading, setPosition, setSearch, setSearched, sku, storage]
  )

  const handleStock = useCallback(
    async (sku: string) => {
      if (!sku) return

      if (storesWithinSearch.length === 0) {
        setStoresWithStock([])
        return
      }

      setLoading(true)
      const storeIds = storesWithinSearch.map(({ storeId }) => storeId)

      const { stores: storesWithStock }: StockPayload = await callFunction("check-stock", {
        products: [sku],
        stores: storeIds,
      })

      const stores = storesWithinSearch.map(store => ({
        ...store,
        stock: storesWithStock?.find(({ storeId }) => storeId === store?.storeId)?.products?.[0]?.stockWithBuffer || 0,
      }))

      setStoresWithStock(stores)

      setTimeout(() => setLoading(false), 700)
    },
    [setStoresWithStock, callFunction, storesWithinSearch]
  )

  const geocodeFromAddress = useCallback(
    async (address: string) => {
      if (address) {
        setLoading(true)
        const { status, body: result }: GeocodeResponse = await callFunction("geocode", {
          type: GeocodeTypes.address,
          address,
        })

        if (status === "success" && result) {
          handlePlace(result)
        } else {
          setErrors(["Could not determine address from search."])
          setLoading(false)
        }
      }
    },
    [callFunction, handlePlace]
  )

  const geocodeFromLocation = useCallback(
    async (location: LatLng) => {
      if (location) {
        setLoading(true)
        const { status, body: result }: GeocodeResponse = await callFunction("geocode", {
          type: GeocodeTypes.location,
          ...location,
        })

        if (status === "success" && result) {
          handlePlace(result)
        } else {
          setErrors(["Could not determine address from location."])
          setLoading(false)
        }
      }
    },
    [callFunction, handlePlace]
  )

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault()

      if (!search) {
        setErrors(["Please enter a suburb or postcode"])

        return
      }

      setErrors([])
      if (showAll) setShowAll(false)
      if (search) geocodeFromAddress(search)
    },
    [geocodeFromAddress, search, setErrors, setShowAll, showAll]
  )

  const handleGeolocate = useCallback(async () => {
    setErrors([])
    setLoading(true)
    navigator.geolocation.getCurrentPosition(
      async (position: any) => {
        if (position?.coords?.latitude && position?.coords?.longitude) {
          geocodeFromLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          })
        } else {
          setErrors([
            "There was an error determining your current location, check that location services are enabled on your device or alternatively use the search field to find your local area.",
          ])
          setLoading(false)
        }
      },
      async () => {
        const response = await fetch(configLocation.serviceUrl)
        const userData = await response.json()

        if (userData?.latitude && userData?.longitude) {
          geocodeFromLocation({
            lat: userData.latitude,
            lng: userData.longitude,
          })
        } else {
          setErrors([
            "There was an error determining your current location, check that location services are enabled on your device or alternatively use the search field to find your local area.",
          ])
          setLoading(false)
        }
      }
    )
  }, [configLocation, geocodeFromLocation, setErrors, setLoading])

  useEffect(() => {
    if (position) storage.set(keys.position, position, 7)
  }, [keys, position, storage])

  useEffect(() => {
    handleStock(sku)
  }, [handleStock, sku, storesWithinSearch])

  useEffect(() => {
    if (!search && storage.get(keys.place)) storage.remove(keys.place)
  }, [keys, search, storage])

  // intentionally only run at first render
  useEffect(() => {
    const saved = storage.get(keys.position)

    if (saved && !position) setPosition(saved)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    errors,
    handleAll,
    handleChange,
    handleGeolocate,
    handleSubmit,
    isLoading,
    position,
    search,
    searched,
    storesWithStock,
    storesWithinSearch,
  }
}
